import { Component, Input, OnChanges, OnInit, Optional } from '@angular/core';
import { NgControl, ValidatorFn } from '@angular/forms';
import { ControlValueAccessor, FormControl } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';

import { allStateCodes, StateCode } from '@digital-platform/shared/domain';

@UntilDestroy()
@Component({
  selector: 'digital-platform-state-select',
  templateUrl: './state-select.component.html',
  styleUrls: ['./state-select.component.scss'],
})
export class StateSelectComponent
  implements OnChanges, OnInit, ControlValueAccessor<string>
{
  @Input() label: string;
  @Input() display: 'code' | 'placeholder' = 'code';
  @Input() country: 'US' | 'CA' | 'MX' = 'US';
  @Input() isDisabled: false;
  @Input() tabIndex: number;
  myControl = new FormControl<string>();
  public onTouched: () => void;

  public type = 'State';
  public placeholder = 'Select State';
  public options: StateCode[] = allStateCodes;

  private touch$ = new Subject<boolean>();

  private originalValidators: ValidatorFn[] = [];

  constructor(@Optional() public controlDir: NgControl) {
    if (this.controlDir !== null) {
      this.controlDir.valueAccessor = this;
    }

    this.updateOptions();
  }

  ngOnChanges(): void {
    this.country = this.country || 'US';
    this.display = this.display || 'code';
    this.updateOptions();
  }

  ngOnInit(): void {
    if (this.controlDir !== null) {
      // ensures validators and validation state gets handled properly, feels a little hacky
      const control = this.controlDir.control;
      let myValidators = [this.validate];
      if (control.validator) {
        this.originalValidators.push(control.validator);
        myValidators = [control.validator, ...myValidators];
        this.myControl.setValidators(control.validator);
        this.myControl.updateValueAndValidity({ emitEvent: false });
      }
      control.setValidators(myValidators);
      control.updateValueAndValidity();
      const markAsTouched = control.markAsTouched.bind(control);
      control.markAsTouched = () => {
        markAsTouched();
        this.myControl.markAsTouched();
      };
      const markAllAsTouched = control.markAllAsTouched.bind(control);
      control.markAllAsTouched = () => {
        markAllAsTouched();
        this.myControl.markAllAsTouched();
      };
    }

    this.myControl.touch$
      .pipe(untilDestroyed(this))
      .subscribe((touch) => this.touch$.next(touch));
  }

  writeValue(value: any): void {
    if (value) {
      this.myControl.setValue(value, { emitEvent: false });
    } else {
      this.myControl.reset();
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.myControl.valueChanges.subscribe(fn);
  }

  public registerOnTouched(fn: () => void) {
    this.touch$.pipe(untilDestroyed(this)).subscribe(fn);
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.myControl.disable();
    } else {
      this.myControl.enable();
    }
  }

  // ensures validators and validation state gets handled properly, feels a little hacky
  validate: ValidatorFn = (c) => {
    this.myControl.updateValueAndValidity({
      onlySelf: false,
      emitEvent: false,
    });

    if (this.myControl.valid) {
      return null;
    }

    return this.myControl.errors;
  };

  private updateOptions() {
    switch (this.country) {
      case 'US':
        this.type = 'State';
        this.placeholder = 'Select State';
        break;
      case 'CA':
        this.type = 'Province';
        this.placeholder = 'Select Province';
        break;
      case 'MX':
        this.type = 'State';
        this.placeholder = 'Select State';
        break;
      default:
        this.type = 'State';
        this.placeholder = 'Select State';
    }

    this.options = allStateCodes.filter(
      (state) =>
        state.country === this.country ||
        (this.country === 'US' && !state.country)
    );
  }

  hasError = (error: string) => {
    if (this.myControl.touched && this.myControl.hasError(error)) {
      this.myControl.updateValueAndValidity();
      return true;
    }
    return false;
  };
}
